import React, { useEffect, useState } from 'react';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import TypedOption from 'components/form/TypedOption.component';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import Icon from 'components/generic/Icon.component';
import STAFF_NAMES from 'constants/settings/staff.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import C from 'utils/values/constant.util';

import SpeedFeedback from 'settings/user/SpeedFeedback.component';
import SuccessfulText from 'settings/user/SuccessfulText.component';
import WarningText from 'settings/user/WarningText.component';

import CN from './NetworkSpeedChecker.module.scss';

const WEAK_SPEED = 9;

const SPEED = C({
  weak: '0 - 10 Mbps',
  slow: '10 - 15 Mbps',
  medium: '15 - 30 Mbps',
  fast: '30 - 40 Mbps',
  faster: '40+ Mbps',
});

const SPEED_INFO = C({
  [SPEED.weak]: { speed: 1 },
  [SPEED.slow]: { speed: 11 },
  [SPEED.medium]: { speed: 16 },
  [SPEED.fast]: { speed: 31 },
  [SPEED.faster]: { speed: 41 },
});

const SPEED_THRESHOLDS = {
  [STAFF_NAMES.staff_1_3]: 10,
  [STAFF_NAMES.staff_4_6]: 15,
  [STAFF_NAMES.staff_7_10]: 30,
  [STAFF_NAMES.staff_11_plus]: 40,
};

const NetworkSpeedChecker = ({ selectedOption, onRunTest }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const [speedRange, setSpeedRange] = useState('');
  const { val: isDropdownRendered, nay: closeDropdown, tip: toggleDropdown } = useTip(false);

  const speedInfo = SPEED_INFO[speedRange];

  const getSpeedMessage = () => {
    if (!speedInfo) return null;
    const speed = parseFloat(speedInfo.speed);

    const threshold = SPEED_THRESHOLDS[selectedOption];

    if (speed >= threshold) {
      return <SuccessfulText />;
    }

    return <WarningText />;
  };

  const handleDropdownSelect = (range) => {
    setSpeedRange(range);
    closeDropdown();
  };

  useEffect(() => {
    setSpeedRange('');
  }, [selectedOption]);

  return (
    <div
      data-bem="NetworkSpeedChecker"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div className={CN.titleContainer}>
        <div className={CN.title}>
          <div className={CN.iconContainer}>
            <Icon variant="wifi" width="xl" />
          </div>
          {t('staffAndBusiness.userNetwork.hardwareInformation.section4Title')}
        </div>
        <div className={CN.runButtonContainer}>
          <Button onClick={onRunTest} variant="primary" xSpace>
            {t('staffAndBusiness.userNetwork.hardwareInformation.testSpeedButton')}
          </Button>
        </div>
      </div>
      <div className={CN.text}>{t('staffAndBusiness.userNetwork.hardwareInformation.section4Description')}</div>

      <div className={CN.resultContainer}>
        <div className={CN.speedInfoContainer}>
          <div className={CN.label}>{t('staffAndBusiness.userNetwork.hardwareInformation.internetSpeed')}</div>
          <div className={CN.dropdownContainer}>
            <div
              onClick={toggleDropdown}
              className={cn({
                [CN.dropdownButton]: true,
                [CN.active]: isDropdownRendered,
              })}
            >
              <div> {speedRange || t('staffAndBusiness.userNetwork.hardwareInformation.selectPeedRange')}</div>
              <Icon variant="arrowTriangle" width="xs" />
            </div>

            {isDropdownRendered && (
              <DropdownFoldout width="full" position="bottom">
                <DropdownFoldout.Body>
                  <div className={CN.dropdownContent}>
                    <div className={CN.item}>
                      <TypedOption type="simple-checkbox" checked={speedRange === SPEED.weak} onCheck={() => handleDropdownSelect(SPEED.weak)}>
                        <div className={CN.name}>{SPEED.weak}</div>
                      </TypedOption>
                    </div>
                    <div className={CN.item}>
                      <TypedOption type="simple-checkbox" checked={speedRange === SPEED.slow} onCheck={() => handleDropdownSelect(SPEED.slow)}>
                        <div className={CN.name}>{SPEED.slow}</div>
                      </TypedOption>
                    </div>
                    <div className={CN.item}>
                      <TypedOption type="simple-checkbox" checked={speedRange === SPEED.medium} onCheck={() => handleDropdownSelect(SPEED.medium)}>
                        <div className={CN.name}>{SPEED.medium}</div>
                      </TypedOption>
                    </div>
                    <div className={CN.item}>
                      <TypedOption type="simple-checkbox" checked={speedRange === SPEED.fast} onCheck={() => handleDropdownSelect(SPEED.fast)}>
                        <div className={CN.name}>{SPEED.fast}</div>
                      </TypedOption>
                    </div>
                    <div className={CN.item}>
                      <TypedOption type="simple-checkbox" checked={speedRange === SPEED.faster} onCheck={() => handleDropdownSelect(SPEED.faster)}>
                        <div className={CN.name}>{SPEED.faster}</div>
                      </TypedOption>
                    </div>
                  </div>
                </DropdownFoldout.Body>
              </DropdownFoldout>
            )}
          </div>
        </div>

        <div className={CN.speedResultContainer}>
          <div className={CN.label}>{t('staffAndBusiness.userNetwork.hardwareInformation.analysisSpeed')}</div>
          {speedInfo ? getSpeedMessage() : <div className={CN.message}>{t('staffAndBusiness.userNetwork.hardwareInformation.analysisInputPlaceholder')}</div>}
        </div>
      </div>
      <div className={CN.titleContainer}>
        <div className={CN.subTitle}>{t('staffAndBusiness.userNetwork.hardwareInformation.nextSteps')}</div>
      </div>
      {speedInfo && <SpeedFeedback speed={speedInfo.speed} threshold={SPEED_THRESHOLDS[selectedOption] || WEAK_SPEED} />}
    </div>
  );
};

export default NetworkSpeedChecker;
